/**
 * AddressesSection Component
 *
 * Displays and manages addresses for a company.
 * Supports add/delete operations with inline forms.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { companiesApi } from '@/services/contactsApi';
import type { CompanyAddress, CompanyAddressInput } from '../types';

interface AddressesSectionProps {
  addresses: CompanyAddress[];
  companyId: string;
  onUpdate: () => void;
  onError: (error: string) => void;
}

export default function AddressesSection({
  addresses,
  companyId,
  onUpdate,
  onError,
}: AddressesSectionProps) {
  const { t } = useTranslation('people');
  const [showAdd, setShowAdd] = useState(false);
  const [newAddress, setNewAddress] = useState<CompanyAddressInput>({
    address_type: 'headquarters',
    is_primary: false,
    street_address: '',
    city: '',
    state: '',
    postal_code: '',
    country: '',
  });

  const handleAdd = async () => {
    try {
      await companiesApi.addAddress(companyId, newAddress);
      onUpdate();
      setShowAdd(false);
      setNewAddress({
        address_type: 'headquarters',
        is_primary: false,
        street_address: '',
        city: '',
        state: '',
        postal_code: '',
        country: '',
      });
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Failed to add address');
    }
  };

  const handleDelete = async (addressId: string) => {
    if (!confirm('Delete this address?')) return;

    try {
      await companiesApi.deleteAddress(companyId, addressId);
      onUpdate();
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Failed to delete address');
    }
  };

  const addressTypes = ['headquarters', 'office', 'warehouse', 'billing'];

  return (
    <div>
      <label className="block text-xs font-medium text-gray-500 uppercase mb-2">{t('sections.addresses.label')}</label>
      <div className="space-y-3">
        {addresses && addresses.length > 0 ? (
          addresses.map((address) => (
            <div key={address.id} className="flex items-start gap-2 text-sm group bg-gray-50 p-3 rounded-lg">
              <button
                onClick={() => handleDelete(address.id)}
                className="text-gray-400 hover:text-red-500 transition-colors p-1 flex-shrink-0"
                title={t('sections.addresses.delete_address')}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-medium text-gray-500 uppercase">{address.address_type}</span>
                  {address.is_primary && (
                    <span className="text-xs bg-yellow-100 text-yellow-700 px-1.5 py-0.5 rounded">primary</span>
                  )}
                </div>
                {address.street_address && <p className="text-gray-700">{address.street_address}</p>}
                <p className="text-gray-700">
                  {[address.city, address.state, address.postal_code].filter(Boolean).join(', ')}
                </p>
                {address.country && <p className="text-gray-500">{address.country}</p>}
              </div>
            </div>
          ))
        ) : (
          <span className="text-gray-400 text-sm">{t('sections.addresses.no_addresses')}</span>
        )}

        {/* Add Address Form */}
        {showAdd ? (
          <div className="mt-2 p-3 bg-gray-50 rounded-lg space-y-3">
            <div className="flex gap-2">
              <select
                value={newAddress.address_type}
                onChange={(e) => setNewAddress({ ...newAddress, address_type: e.target.value })}
                className="px-2 py-1.5 text-sm border border-gray-300 rounded"
              >
                {addressTypes.map((type) => (
                  <option key={type} value={type}>{type.charAt(0).toUpperCase() + type.slice(1)}</option>
                ))}
              </select>
              <label className="flex items-center gap-1 text-sm text-gray-600">
                <input
                  type="checkbox"
                  checked={newAddress.is_primary}
                  onChange={(e) => setNewAddress({ ...newAddress, is_primary: e.target.checked })}
                />
                {t('sections.addresses.primary')}
              </label>
            </div>
            <input
              type="text"
              value={newAddress.street_address || ''}
              onChange={(e) => setNewAddress({ ...newAddress, street_address: e.target.value })}
              placeholder={t('sections.addresses.placeholders.street_address')}
              className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <div className="grid grid-cols-2 gap-2">
              <input
                type="text"
                value={newAddress.city || ''}
                onChange={(e) => setNewAddress({ ...newAddress, city: e.target.value })}
                placeholder={t('sections.addresses.placeholders.city')}
                className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <input
                type="text"
                value={newAddress.state || ''}
                onChange={(e) => setNewAddress({ ...newAddress, state: e.target.value })}
                placeholder={t('sections.addresses.placeholders.state')}
                className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <input
                type="text"
                value={newAddress.postal_code || ''}
                onChange={(e) => setNewAddress({ ...newAddress, postal_code: e.target.value })}
                placeholder={t('sections.addresses.placeholders.postal_code')}
                className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <input
                type="text"
                value={newAddress.country || ''}
                onChange={(e) => setNewAddress({ ...newAddress, country: e.target.value })}
                placeholder={t('sections.addresses.placeholders.country')}
                className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleAdd}
                className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                {t('sections.addresses.add')}
              </button>
              <button
                onClick={() => {
                  setShowAdd(false);
                  setNewAddress({
                    address_type: 'headquarters',
                    is_primary: false,
                    street_address: '',
                    city: '',
                    state: '',
                    postal_code: '',
                    country: '',
                  });
                }}
                className="px-3 py-1 text-sm text-gray-600 hover:bg-gray-200 rounded"
              >
                {t('sections.addresses.cancel')}
              </button>
            </div>
          </div>
        ) : (
          <button
            onClick={() => setShowAdd(true)}
            className="mt-1 text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            {t('sections.addresses.add_address')}
          </button>
        )}
      </div>
    </div>
  );
}
